import React from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { format, parseISO } from "date-fns";
import { IconFiles, IconInfoCircle, IconShare3 } from "@tabler/icons-react";

import MHeader from "@/components/ui/organisms/MHeader";
import MBadge from "@/components/ui/atoms/MBadge";
import { MButton } from "@/components/ui/atoms/MButtons";
import MLoader from "@/components/ui/atoms/MLoader";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { useLabInsightsParameterDetails } from "@/hooks/useLabInsights";
import { popPathSegment } from "@/utils/path-utils";
import { triggerNativeLabInsightsParameterShare } from "@/utils/nativeBridge";
import labInsightsService from "@/services/lab-insights.service";
import {
  HistoricalReading,
  ParameterStatus,
  RangeBar as RangeBarDto,
} from "@/types/lab-insights";
import { computeRangeBarViewport } from "@/utils/lab-range-bar";

import styles from "./LabInsightsParameterDetails.module.css";

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "dd MMM yyyy");
  } catch {
    return "";
  }
};

/**
 * Title-cases a lab name for display. OCR often hands us facility names in
 * shouting case ("DR.DANGS LAB", "HEALTHPLUS CLINIC"); rendering those
 * verbatim looks aggressive next to the rest of the muted typography. The
 * regex re-cases each word boundary — dots and hyphens count as
 * boundaries — so "DR.DANGS LAB" becomes "Dr.Dangs Lab" rather than
 * "Dr.dangs Lab". Pure display helper; the stored labName field is
 * unchanged.
 */
const formatLabName = (raw: string | null | undefined): string => {
  if (!raw) return "";
  return raw.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const valueToneClass = (
  status: ParameterStatus,
  flagged: boolean,
  baseClass: string,
  flaggedClass: string,
  okClass: string
): string => {
  if (flagged) return flaggedClass;
  if (status === ParameterStatus.NORMAL) return okClass;
  return baseClass;
};

const buildShareText = (
  parameterName: string,
  history: HistoricalReading[]
): string => {
  const lines = [
    `${parameterName} — past results`,
    ...history.map((h) => {
      const date = formatDate(h.reportDate);
      const lab = h.labName ?? "";
      const value =
        h.parsedResult !== null && h.parsedResult !== undefined
          ? `${h.parsedResult}${h.unit ? ` ${h.unit}` : ""}`
          : h.rawResult ?? "";
      const range = h.rangeText ?? "";
      const flag = h.flagged ? " (flagged)" : "";
      return `${date} • ${lab} • ${value}${flag}${range ? ` • Range ${range}` : ""}`;
    }),
  ];
  return lines.filter(Boolean).join("\n");
};

const LabInsightsParameterDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const { canonicalParameterId: rawId } =
    useParams<{ canonicalParameterId: string }>();
  const [searchParams] = useSearchParams();

  // Unmatched OTHERS rows arrive as `/lab-insights/parameters/_raw?raw=<name>&group=<id>`.
  // The "_raw" sentinel means there's no canonical id — drive Screen 3 by the
  // raw OCR name instead. Matched rows arrive with a real canonical id and no
  // raw query param.
  const isRawMode = rawId === "_raw";
  const canonicalParameterId = isRawMode ? null : rawId ?? null;
  const rawName = isRawMode ? searchParams.get("raw") : null;
  const rawGroupId = isRawMode ? searchParams.get("group") : null;

  const { data, loading, error } = useLabInsightsParameterDetails(
    memberInfo?.id,
    canonicalParameterId,
    rawName,
    rawGroupId
  );

  const handleBack = () => {
    // If the navigator passed a `from` path (group details, landing, etc.),
    // honour it so back returns to the exact origin screen. Otherwise fall
    // back to URL-segment popping (`/lab-insights/parameters/:id` →
    // `/lab-insights/parameters` → redirected to `/lab-insights`).
    const fromPath = (location.state as { from?: string } | null)?.from;
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    navigate(popPathSegment(location.pathname));
  };

  const handleCopy = async () => {
    if (!data) return;
    const text = buildShareText(data.canonicalName, data.history);
    try {
      await navigator.clipboard.writeText(text);
      showToast({ message: t("labInsights.copied"), variant: "success" });
    } catch {
      showToast({ message: t("labInsights.copyFailed"), variant: "error" });
    }
  };

  const handleShare = async () => {
    if (!data || !memberInfo?.id || !canonicalParameterId) return;

    const memberName =
      memberInfo.nickname ||
      [memberInfo.firstName, memberInfo.lastName].filter(Boolean).join(" ") ||
      "";

    // 1. Native webview: ask the shell to fetch the PDF and trigger the OS share
    //    sheet — same path as emergency-card / wallet-card / health-summary.
    const handed = triggerNativeLabInsightsParameterShare({
      memberId: memberInfo.id,
      memberName,
      canonicalParameterId,
      parameterName: data.canonicalName,
    });
    if (handed) return;

    // 2. Web: download the PDF and try the Web Share API with a file payload.
    //    Falls back to a plain download (then copy) when share-with-file isn't
    //    available.
    try {
      const blob = await labInsightsService.downloadParameterPdf(
        memberInfo.id,
        canonicalParameterId
      );
      const filename = `lab-insights-${data.canonicalName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}.pdf`;
      const file = new File([blob], filename, { type: "application/pdf" });

      const navAny = navigator as any;
      if (
        navAny.canShare &&
        navAny.canShare({ files: [file] }) &&
        navigator.share
      ) {
        await navigator.share({
          title: `${data.canonicalName} — Lab Insights`,
          files: [file],
        });
        return;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // If PDF generation/download fails, fall back to the previous text share.
      const text = buildShareText(data.canonicalName, data.history);
      if (navigator.share) {
        try {
          await navigator.share({ title: data.canonicalName, text });
          return;
        } catch {
          // user dismissed — no-op
        }
      }
      await handleCopy();
    }
  };

  return (
    <div className={styles.page}>
      <MHeader
        module="labInsights"
        subHeading={data?.canonicalName ?? t("labInsights.title")}
        memberInfo={memberInfo}
        hideLearnMore
        onBack={handleBack}
        className={styles.flushHeader}
      />

      {loading && !data && <MLoader />}

      {error && !loading && (
        <p className={styles.emptyState}>{t("labInsights.errorLoading")}</p>
      )}

      {data && !loading && (
        <div className={styles.body}>
          {data.patientDescription && (
            <div className={styles.infoCard}>
              <IconInfoCircle size={24} className={styles.infoIcon} />
              <p className={styles.infoText}>{data.patientDescription}</p>
            </div>
          )}

          {data.rangeBar && (
            <>
              <hr className={styles.divider} />
              <ReadingCard
                rangeBar={data.rangeBar}
                parameterName={data.canonicalName}
                reportDate={data.history[0]?.reportDate ?? null}
              />
            </>
          )}

          {/* Interpretation card is intentionally hidden for now — Figma
              still shows it (2739:24202) but the product spec is to omit
              this section until the AI copy is finalized. Keep the JSX
              commented (not deleted) so re-enabling is one un-comment away
              when the warning-styled card returns.
          {data.interpretation && (
            <div className={styles.interpretationCard}>
              <h3 className={styles.interpretationTitle}>
                {t("labInsights.interpretation")}
              </h3>
              <p className={styles.interpretationText}>{data.interpretation}</p>
            </div>
          )} */}

          {/* Figma 2739:24206 — a second divider sits above the Past
              results header. Render it only when both the reading card and
              past results are present, otherwise it'd float in space. */}
          {data.rangeBar && data.history.length > 0 && (
            <hr className={styles.divider} />
          )}

          {data.history.length > 0 && (() => {
            // The latest reading is already rendered above in the ReadingCard.
            // "Past Results" should list everything earlier than that. When the
            // member has only one reading, there are no past results to show —
            // surface a friendly empty state instead of repeating the current.
            const pastReadings = data.history.slice(1);
            return (
            <section className={styles.pastResultsSection}>
              <div className={styles.pastResultsHeader}>
                <h2 className={styles.pastResultsTitle}>
                  {t("labInsights.pastResults")}
                </h2>
                {pastReadings.length > 0 && (
                  <div className={styles.pastResultsActions}>
                    <MButton
                      variant="outline"
                      size="sm"
                      onClick={handleCopy}
                      rightIcon={<IconFiles size={12} />}
                      className={styles.pastResultActionBtn}
                    >
                      {t("labInsights.copy")}
                    </MButton>
                    {/* Share generates a PDF keyed on the canonical id.
                        Unmatched OTHERS rows (raw-name mode) have no canonical
                        id and no PDF endpoint, so hide Share there — Copy
                        still works since it builds text from `data`. */}
                    {!isRawMode && (
                      <MButton
                        variant="outline"
                        size="sm"
                        onClick={handleShare}
                        rightIcon={<IconShare3 size={12} />}
                        className={styles.pastResultActionBtn}
                      >
                        {t("labInsights.share")}
                      </MButton>
                    )}
                  </div>
                )}
              </div>

              {pastReadings.length === 0 ? (
                <p className={styles.emptyState}>
                  {t("labInsights.noPastResults")}
                </p>
              ) : (
                pastReadings.map((reading, idx) => (
                  <PastResultRow
                    key={`${reading.reportId}-${idx}`}
                    reading={reading}
                  />
                ))
              )}
            </section>
            );
          })()}

        </div>
      )}
    </div>
  );
};

interface ReadingCardProps {
  rangeBar: RangeBarDto;
  parameterName: string;
  reportDate: string | null;
}

const ReadingCard: React.FC<ReadingCardProps> = ({
  rangeBar,
  parameterName,
  reportDate,
}) => {
  const { t } = useTranslation();
  const flagged = rangeBar.flagged;
  const viewport = computeRangeBarViewport(
    rangeBar.rangeLow,
    rangeBar.rangeHigh,
    rangeBar.parsedResult
  );
  const valueToneClassName = valueToneClass(
    rangeBar.status,
    flagged,
    styles["readingValue--unknown"],
    styles["readingValue--flagged"],
    styles["readingValue--allGood"]
  );

  return (
    <div className={styles.readingCard}>
      <div className={styles.readingTopRow}>
        <span className={styles.readingDate}>{formatDate(reportDate)}</span>
        {flagged ? (
          <MBadge variant="red">{t("labInsights.flagged")}</MBadge>
        ) : rangeBar.status === ParameterStatus.NORMAL ? (
          <MBadge variant="green">{t("labInsights.good")}</MBadge>
        ) : null}
      </div>

      <div className={styles.readingCenter}>
        <span className={styles.readingParamName}>{parameterName}</span>
        <span className={styles.readingValueRow}>
          <span
            className={[styles.readingValue, valueToneClassName]
              .filter(Boolean)
              .join(" ")}
          >
            {rangeBar.parsedResult ?? rangeBar.rawResult ?? "—"}
          </span>
          {rangeBar.unit && (
            <span className={styles.readingUnit}>{rangeBar.unit}</span>
          )}
        </span>
      </div>

      {(viewport !== null ||
        rangeBar.rangeLow !== null ||
        rangeBar.rangeHigh !== null) && (
        <div className={styles.indicator}>
          <div className={styles.indicatorBar}>
            {viewport && (
              <div
                className={styles.rangeArea}
                style={{
                  left: `${viewport.inRangeStartPct}%`,
                  right: `${100 - viewport.inRangeEndPct}%`,
                }}
              />
            )}
            {viewport?.valuePct !== null && viewport?.valuePct !== undefined && (
              <span
                className={styles.indicatorDot}
                style={{ left: `${viewport.valuePct}%` }}
                aria-hidden
              />
            )}
          </div>
          <div className={styles.indicatorValues}>
            {rangeBar.rangeLow !== null && rangeBar.rangeLow !== undefined && (
              <span
                className={styles.indicatorValue}
                style={
                  viewport && Number.isFinite(viewport.inRangeStartPct)
                    ? { left: `${viewport.inRangeStartPct}%` }
                    : { left: 0, transform: "none" }
                }
              >
                {rangeBar.rangeLow}
              </span>
            )}
            {rangeBar.rangeHigh !== null && rangeBar.rangeHigh !== undefined && (
              <span
                className={styles.indicatorValue}
                style={
                  viewport && Number.isFinite(viewport.inRangeEndPct)
                    ? { left: `${viewport.inRangeEndPct}%` }
                    : { left: "auto", right: 0, transform: "none" }
                }
              >
                {rangeBar.rangeHigh}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface PastResultRowProps {
  reading: HistoricalReading;
}

const PastResultRow: React.FC<PastResultRowProps> = ({ reading }) => {
  const { t } = useTranslation();
  const valueToneClassName = reading.flagged
    ? styles["pastResultValue--flagged"]
    : styles["pastResultValue--ok"];

  const rangeText =
    reading.rangeText ??
    (reading.rangeLow !== null && reading.rangeHigh !== null
      ? `${reading.rangeLow} - ${reading.rangeHigh}`
      : "");

  return (
    <div className={styles.pastResultRow}>
      <div className={styles.pastResultLeft}>
        <span className={styles.pastResultDate}>
          {formatDate(reading.reportDate)}
        </span>
        {reading.labName && (
          <span className={styles.pastResultLab}>
            {formatLabName(reading.labName)}
          </span>
        )}
        {rangeText && (
          <span className={styles.pastResultRange}>
            {t("labInsights.rangePrefix")}{" "}
            <span className={styles.pastResultRangeValue}>{rangeText}</span>
          </span>
        )}
      </div>
      <div className={styles.pastResultRight}>
        <span className={styles.pastResultValueRow}>
          <span
            className={[styles.pastResultValue, valueToneClassName]
              .filter(Boolean)
              .join(" ")}
          >
            {reading.parsedResult ?? reading.rawResult ?? "—"}
          </span>
          {reading.unit && (
            <span className={styles.pastResultUnit}>{reading.unit}</span>
          )}
        </span>
        {reading.flagged && (
          <MBadge variant="red">{t("labInsights.flagged")}</MBadge>
        )}
      </div>
    </div>
  );
};

export default LabInsightsParameterDetails;
